/**
 * Retail POS Payment Allocation
 *
 * Pure UX projection contract for a Payment intent against the
 * authoritative Receipt remaining amount.
 *
 * This module does NOT own:
 *
 * - Receipt state;
 * - Payment state;
 * - paid;
 * - remaining;
 * - total_ttc;
 * - persistence;
 * - Payment execution;
 * - Payment allocation persistence;
 * - Receipt completion.
 *
 * `remaining` is an authoritative value received from the Receipt.
 * `requestedAmount` is an UX intent.
 */
export function resolveRetailPOSPaymentAllocation({
  remaining,
  requestedAmount,
} = {}) {
  const normalizedRemaining =
    normalizePositiveMoney(
      remaining,
      "remaining"
    );

  const normalizedRequestedAmount =
    normalizePositiveMoney(
      requestedAmount,
      "requestedAmount"
    );

  if (
    normalizedRequestedAmount
      > normalizedRemaining
  ) {
    throw new RangeError(
      "requestedAmount cannot exceed remaining."
    );
  }

  return Object.freeze({
    amount:
      normalizedRequestedAmount,

    isPartial:
      normalizedRequestedAmount
        < normalizedRemaining,

    completesReceipt:
      normalizedRequestedAmount
        === normalizedRemaining,
  });
}


function normalizePositiveMoney(
  value,
  fieldName
) {
  const amount =
    Number(
      typeof value === "string"
        ? value
            .trim()
            .replace(",", ".")
        : value
    );

  if (
    !Number.isFinite(amount)
    || amount <= 0
  ) {
    throw new TypeError(
      `${fieldName} must be a positive number.`
    );
  }

  return roundMoney(
    amount
  );
}


function roundMoney(
  value
) {
  return (
    Math.round(
      (
        Number(value)
        + Number.EPSILON
      )
      * 100
    )
    / 100
  );
}
